import React, { FC, useCallback, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { NavLink } from 'react-router-dom'
import { Grid, Typography } from '@mui/material'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { useDispatch, useSelector } from 'react-redux'
import { GreenButton } from '../../../components/Buttons/GreenButton'
import { useAuth2FA } from '../../../components/Context'
import { InputCodeField } from '../../../components/Dashboard/InputCodeField'
import { routes } from '../../../constants/routes'
import { Loader } from '../../../components/Loader'
import { isUserLoggingInSelector } from '../../../store/selectors'
import { login2FARequest } from '../../../store/actions'

/**
 * Custom styles for the TwoFactorAuthentication component
 * @param {Theme} theme - Material-UI theme object
 * @returns {Object} Style object with CSS classes
 */
const useStyles = makeStyles((theme: Theme) => ({
  main: {
    width: '100%',
    minHeight: 'calc(100vh - 248px)',
    padding: '155px 0',
  },
  container: {
    justifyContent: 'center',
    maxWidth: 1184,
    margin: '0 auto',
    paddingTop: 64,
    borderTop: '1px solid rgba(0,0,0,0.24)',
    textAlign: 'center',
  },
  title: {
    padding: '25px 0',
    marginTop: '0 auto',
    fontSize: 40,
    fontWeight: 'bold',
    letterSpacing: 0,
    color: theme.palette.primary.dark,
  },
  code: {
    width: '280px',
    paddingLeft: '0 !important',
    margin: '0 auto',
  },
  error: {
    color: '#E02020',
    fontWeight: 500,
    paddingTop: theme.spacing(2),
  },
  link: {
    color: '#584DE2',
  },
}))

/**
 * Type definition for form inputs
 * @property {string} twoFaCode - The 2FA code entered by the user
 */
type Inputs = {
  twoFaCode: string
}

export const TwoFactorAuthentication: FC = () => {
  const formMethods = useForm<Inputs>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldFocusError: true,
  })
  const { watch, handleSubmit } = formMethods
  const { removeAuth2FA } = useAuth2FA()
  const [isError, setIsError] = useState<boolean>(false)
  const [code, setCode] = useState<string | undefined>('')
  const [isDisabled, setIsDisabled] = useState<boolean>(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const dispatch = useDispatch()
  const isUserLoggingIn = useSelector(isUserLoggingInSelector)

  const classes = useStyles({ isError })

  /**
   * Effect hook that watches for changes in the 2FA code input field
   * Validates the code length and updates the form state accordingly
   * - Disables form submission if code length is not 6 digits
   * - Enables form submission when valid 6-digit code is entered
   * - Handles subscription cleanup on unmount
   */
  useEffect(() => {
    const subscription = watch((value) => {
      setCode(value.twoFaCode)
      if (value?.twoFaCode?.length !== 6) {
        setIsDisabled(true)
        setIsError(false)
      } else {
        setIsDisabled(false)
      }
      return () => subscription.unsubscribe()
    })
  }, [watch])

  /**
   * Handles the form submission for 2FA verification
   * - Prevents multiple submissions using isSubmitting flag
   * - Makes an API call to verify the 2FA code
   * - On success, saves the authentication token and user data
   * - On error, displays appropriate error messages
   * - Handles cleanup after submission (resets form, loading states, etc.)
   *
   * @returns {Promise<void>}
   */
  const onSubmit = useCallback(() => {
    if (isSubmitting) return

    if (code) {
      setIsSubmitting(true)

      dispatch(login2FARequest({ twoFaCode: code }))

      formMethods.reset()
    }
  }, [dispatch, isSubmitting, formMethods, code])

  /**
   * Handles the Enter key press event in the 2FA input field
   * Submits the form when Enter key is pressed if the form is valid
   *
   * @param {boolean} isEnterPresses - Indicates if the Enter key was pressed
   * @returns {void}
   */
  const getPressEnterEvent = useCallback(
    (isEnterPresses: boolean) => {
      if (isEnterPresses) onSubmit()
    },
    [onSubmit],
  )

  /**
   * Cleanup effect that runs when the component unmounts
   * Removes the 2FA authentication flag from the auth context
   */
  useEffect(() => {
    return () => {
      removeAuth2FA()
    }
    // eslint-disable-next-line
  }, [])

  return (
    <>
      {isUserLoggingIn && <Loader />}

      <main className={classes.main}>
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={4} className={classes.container}>
              <Typography variant="h1" gutterBottom className={classes.title}>
                Two-Factor Authentication
              </Typography>

              <Grid item xs={12}>
                <Typography variant="h2">Enter the code from Google Authenticator</Typography>
              </Grid>

              <Grid item xs={12}>
                <Grid className={classes.code}>
                  <InputCodeField width={40} height={40} isError={isError} getPressEnterEvent={getPressEnterEvent} />
                </Grid>

                {isError && <Typography className={classes.error}>Incorrect two-factor authentication code</Typography>}
              </Grid>

              <Grid item xs={12}>
                <Typography>
                  If you have any difficulties with authentication, please{' '}
                  <NavLink className={classes.link} to={routes.CONTACT}>
                    contact support
                  </NavLink>
                  .
                </Typography>
              </Grid>

              <GreenButton type="submit" width={320} mt={72} text="Verify" disabled={isDisabled || isError} />
            </Grid>
          </form>
        </FormProvider>
      </main>
    </>
  )
}
